import {
  KUIKA_BUTTON_ERROR_HOLD_MS,
  KUIKA_BUTTON_SUCCESS_HOLD_MS,
  type KuikaButtonStatus
} from "../../components/ui-kit/kuika-button/kuika-button";

/**
 * A stateful commit, as a state machine: idle → loading → success | error → idle.
 *
 * The designer's saves were written as a boolean set true on submit, the surface closed in the
 * success branch, and an unconditional reset after the `await` (or an `onFinal`) covering every
 * path back. A stateful commit cannot be written that way — the trailing reset fires in the same
 * tick as the ✓ and erases it before it is painted, and a surface closed in the success branch
 * takes the ✓ with it.
 *
 * So the order is fixed here rather than in every save: the ✓ goes up, it is held long enough to
 * be read (`KUIKA_BUTTON_SUCCESS_HOLD_MS`), and only then does `succeed`'s callback run — that is
 * where the close goes. The button returns to `idle` AFTER the callback: the other way round it
 * repaints its idle label first (a timer callback is not batched on React 17) and the surface plays
 * its exit with the label instead of the ✓. Callers must terminate the machine on EVERY exit — a
 * branch that neither succeeds, fails nor resets leaves the button on "Saving".
 *
 * The surfaces are mostly class components, which is why this is a small object rather than a
 * hook; a function component holds it in a `useRef`. Settings dialogs, datasource editors, tool
 * headers and management dialogs all run this one machine.
 */
export class SaveStatus {
  private status: KuikaButtonStatus = "idle";
  private timer: number | undefined = undefined;
  /** Set by `dispose`: the host is gone, so nothing reaches `onChange` any more. */
  private isDisposed: boolean = false;

  /** @param onChange Re-renders the host — `(saveStatus) => this.setState({ saveStatus })`. */
  constructor(private readonly onChange: (status: KuikaButtonStatus) => void) {}

  /** What the host hands to the header. */
  get current(): KuikaButtonStatus {
    return this.status;
  }

  /**
   * Whether a commit may start: `false` while one is running AND while its ✓ is up. `KuikaButton`
   * refuses a click only while `loading`, so during the success hold a submit button is still live
   * and a second Enter would re-fire the save behind the closing editor.
   */
  get canStart(): boolean {
    return this.status === "idle" || this.status === "error";
  }

  /** The commit has started. */
  start = (): void => {
    this.clearTimer();
    this.set("loading");
  };

  /**
   * The commit succeeded. The ✓ is held, then `then` runs — that is where `closeDataSource()` goes,
   * so the editor never leaves before its own confirmation has been seen.
   *
   * `then` runs BEFORE the return to idle, never after. The editors re-run their guarded close on
   * every update while a navigation is pending (`canStart` gates it); an idle set first would
   * re-render the host with a free machine, and that update would close the editor a second time
   * before `then` got to. And a host that unmounts inside `then` has disposed the machine, so the
   * idle that follows is not reported to it.
   */
  succeed = (then?: () => void): void => {
    this.clearTimer();
    this.set("success");
    this.timer = window.setTimeout((): void => {
      this.timer = undefined;
      then?.();
      this.set("idle");
    }, KUIKA_BUTTON_SUCCESS_HOLD_MS);
  };

  /** The commit failed. The editor stays open, so the button is where the failure is said. */
  fail = (): void => {
    this.clearTimer();
    this.set("error");
    this.timer = window.setTimeout((): void => {
      this.timer = undefined;
      this.set("idle");
    }, KUIKA_BUTTON_ERROR_HOLD_MS);
  };

  /** Back to `idle` at once, for a caller that has to abandon the commit (a validation bail-out). */
  reset = (): void => {
    this.clearTimer();
    this.set("idle");
  };

  /** `componentWillUnmount`. A timer that fires after the editor is gone would setState on it. */
  dispose = (): void => {
    this.isDisposed = true;
    this.clearTimer();
  };

  private set = (status: KuikaButtonStatus): void => {
    if (this.isDisposed || this.status === status) return;
    this.status = status;
    this.onChange(status);
  };

  private clearTimer = (): void => {
    if (this.timer === undefined) return;
    window.clearTimeout(this.timer);
    this.timer = undefined;
  };
}
